import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import React from 'react';
import type { StageStep } from './types';

/** A record that has left the path: the state it is in, and the step it left from. */
export interface StageExit {
  label: string;
  /** The reason, shown under the label ("Client Rejected"). */
  detail?: string;
  color: string;
  /** Index of the path step the record stopped at, or -1 when that is not known. */
  stoppedIndex: number;
}

interface StageStepperProps {
  stages: StageStep[];
  /** Index of the current stage (0-based). Stages before it render completed. */
  currentIndex: number;
  /**
   * For a long path (the ten-stage hiring workflow): on a phone each step keeps a readable width
   * and the row scrolls, instead of every label shrinking to an ellipsis.
   */
  scroll?: boolean;
  /**
   * Set when the record is off the path (a closed candidate). The path stays lit up to where they
   * stopped, that step is marked as stopped rather than current, and the exit is drawn after the
   * path as a branch. `currentIndex` is ignored while this is set.
   */
  exit?: StageExit | null;
}

/**
 * Read-only stage visualisation. The status <select> is the source of truth;
 * this is derived from statusToStage() and never mutates state.
 */
const StageStepper: React.FC<StageStepperProps> = ({ stages, currentIndex, scroll = false, exit = null }) => {
  // Off the path, the step they stopped at plays the "current" role for the done/future split.
  const reached = exit ? exit.stoppedIndex : currentIndex;
  return (
    <Box className={`cc-stepper${scroll ? ' cc-stepper--scroll' : ''}${exit ? ' cc-stepper--exited' : ''}`}>
      {stages.map((stage, i) => {
        const done = i < reached;
        const current = i === reached && !exit;
        const stopped = i === reached && !!exit;
        const state = done ? 'done' : current ? 'current' : stopped ? 'stopped' : 'future';
        return (
          <Box
            key={stage.id}
            className={`cc-step cc-step--${state}`}
            aria-current={current ? 'step' : undefined}
          >
            <Box className="cc-step__track">
              {i > 0 && (
                <span
                  className="cc-step__line cc-step__line--left"
                  style={i <= reached ? { background: 'var(--green)' } : undefined}
                />
              )}
              <span
                className="cc-step__dot"
                style={
                  done
                    ? { background: 'var(--green)', borderColor: 'var(--green)' }
                    : current
                      ? { borderColor: stage.color, background: 'var(--ink-800)' }
                      : stopped && exit
                        ? { borderColor: exit.color, background: 'var(--ink-800)' }
                        : undefined
                }
              >
                {done && <CheckIcon className="cc-step__check" />}
              </span>
              {i < stages.length - 1 && (
                <span
                  className="cc-step__line cc-step__line--right"
                  style={i < reached ? { background: 'var(--green)' } : undefined}
                />
              )}
            </Box>
            <span
              className="cc-step__label"
              style={current ? { color: stage.color } : undefined}
            >
              {stage.label}
            </span>
          </Box>
        );
      })}
      {exit && (
        /* The branch: separate from the path, so a candidate closed at Screening does not read as
           having walked through to the end. */
        <Box className="cc-step cc-step--exit" aria-current="step">
          <Box className="cc-step__track">
            <span className="cc-step__dot" style={{ borderColor: exit.color, background: exit.color }} />
          </Box>
          <span className="cc-step__label" style={{ color: exit.color }}>
            {exit.label}
          </span>
          {exit.detail && <span className="cc-step__detail">{exit.detail}</span>}
        </Box>
      )}
    </Box>
  );
};

export default StageStepper;
